import { PayloadAction, createSlice } from '@reduxjs/toolkit';

const AppearanceStorageKey = 'theme';

export enum AppearanceSetting {
  Light = 'Light',
  Dark = 'Dark',
  System = 'System',
}

export const isAppearanceSetting = (value: string): value is AppearanceSetting => {
  return Object.values(AppearanceSetting).includes(value as AppearanceSetting);
};

export interface PrefsData {
  darkTheme: boolean;
  appearanceRaw: AppearanceSetting;
}

const readAppearanceFromStorage = (): AppearanceSetting => {
  try {
    // Get from local storage by key
    const item = window.localStorage.getItem(AppearanceStorageKey);
    if (item) {
      return item === 'true' ? AppearanceSetting.Dark : AppearanceSetting.Light;
    }
  } catch {
    // Fall through
  }

  return AppearanceSetting.System;
};

const writeAppearanceToStorage = (value: AppearanceSetting) => {
  try {
    if (typeof window !== 'undefined') {
      switch (value) {
        case AppearanceSetting.System:
          window.localStorage.removeItem(AppearanceStorageKey);
          break;

        case AppearanceSetting.Dark:
          window.localStorage.setItem(AppearanceStorageKey, 'true');
          break;

        case AppearanceSetting.Light:
          window.localStorage.setItem(AppearanceStorageKey, 'false');
          break;
      }
    }
  } catch {
    /* empty */
  }
};

const isDarkAppearance = (): boolean => {
  const defaultValue = window.matchMedia('(prefers-color-scheme: dark)')?.matches;

  if (typeof window === 'undefined') {
    return defaultValue;
  }

  const setting = readAppearanceFromStorage();
  if (setting === AppearanceSetting.System) {
    return defaultValue;
  } else {
    return setting === AppearanceSetting.Dark;
  }
};

const prefsSlice = createSlice({
  name: 'prefs',
  initialState: {
    darkTheme: isDarkAppearance(),
    appearanceRaw: readAppearanceFromStorage(),
  } as PrefsData,
  reducers: {
    setAppearance: (state, action: PayloadAction<AppearanceSetting>) => {
      writeAppearanceToStorage(action.payload);
      state.darkTheme = isDarkAppearance();
      state.appearanceRaw = readAppearanceFromStorage();
    },
  },
});

export const { setAppearance } = prefsSlice.actions;

export default prefsSlice.reducer;
